import { api } from "@shared/api/client";

export type NominaLinea = {
  descripcion: string;
  importe: number;
  en_especie?: boolean;
  sujeto_cotizacion?: boolean;
};

export type Nomina = {
  id: number;
  usuario_id: number;
  mes: number;
  año: number;
  tipo: string;
  estado: "borrador" | "confirmada";
  dias_trabajados: string | number;
  total_devengo: string | number;
  total_deduccion: string | number;
  base_cc: string | number;
  base_irpf: string | number;
  irpf_porcentaje: string | number;
  irpf_importe: string | number;
  ss_trabajador: string | number;
  liquido: string | number;
  devengos: NominaLinea[] | null;
  deducciones: NominaLinea[] | null;
  observaciones_ia: string | null;
  usuario?: { id: number; nombre: string; correo?: string };
};

export type NominaConcepto = {
  id: number;
  usuario_id: number;
  descripcion: string;
  tipo: "devengo" | "deduccion";
  importe: string | number;
  sujeto_cotizacion: boolean;
  en_especie: boolean;
  orden: number;
  activo: boolean;
};

export type DatosLaborales = {
  id: number;
  nombre: string;
  dni: string | null;
  naf: string | null;
  categoria: string | null;
  grupo_cotizacion: number | null;
  num_pagas: number | null;
  irpf_porcentaje: string | number | null;
  salario_bruto_anual: string | number | null;
  horas_semanales: string | number | null;
  fecha_inicio_contratacion: string | null;
  fecha_fin_contrato: string | null;
  tipo_contrato: string | null;
  conceptos: NominaConcepto[];
};

const BASE = "/rrhh/nominas";

export async function getDatosLaborales(usuarioId: number): Promise<DatosLaborales> {
  const { data } = await api.get<{ data: DatosLaborales }>(
    `${BASE}/empleado/${usuarioId}/datos`,
  );
  return data.data;
}

export async function guardarDatosLaborales(
  usuarioId: number,
  payload: Partial<Omit<DatosLaborales, "id" | "nombre" | "conceptos">>,
): Promise<void> {
  await api.put(`${BASE}/empleado/${usuarioId}/datos`, payload);
}

export async function crearConcepto(
  usuarioId: number,
  payload: Pick<NominaConcepto, "descripcion" | "tipo" | "importe"> &
    Partial<Pick<NominaConcepto, "sujeto_cotizacion" | "en_especie" | "activo">>,
): Promise<NominaConcepto> {
  const { data } = await api.post<{ data: NominaConcepto }>(
    `${BASE}/empleado/${usuarioId}/conceptos`,
    payload,
  );
  return data.data;
}

export async function actualizarConcepto(
  conceptoId: number,
  payload: Pick<NominaConcepto, "descripcion" | "tipo" | "importe"> &
    Partial<Pick<NominaConcepto, "sujeto_cotizacion" | "en_especie" | "activo">>,
): Promise<NominaConcepto> {
  const { data } = await api.patch<{ data: NominaConcepto }>(
    `${BASE}/conceptos/${conceptoId}`,
    payload,
  );
  return data.data;
}

export async function eliminarConcepto(conceptoId: number): Promise<void> {
  await api.delete(`${BASE}/conceptos/${conceptoId}`);
}

export async function listarNominas(mes: number, anio: number): Promise<Nomina[]> {
  const { data } = await api.get<{ data: Nomina[] }>(BASE, {
    params: { mes, anio },
  });
  return data.data;
}

export async function generarNominas(payload: {
  mes: number;
  anio: number;
  usuario_ids?: number[];
}): Promise<{ ok: boolean; generadas: number }> {
  const { data } = await api.post<{ ok: boolean; generadas: number }>(
    `${BASE}/generar`,
    payload,
  );
  return data;
}

export async function getNomina(id: number): Promise<Nomina> {
  const { data } = await api.get<{ data: Nomina }>(`${BASE}/${id}`);
  return data.data;
}

export async function actualizarNomina(
  id: number,
  payload: Partial<
    Pick<
      Nomina,
      | "devengos"
      | "deducciones"
      | "total_devengo"
      | "total_deduccion"
      | "liquido"
      | "observaciones_ia"
    >
  >,
): Promise<Nomina> {
  const { data } = await api.patch<{ data: Nomina }>(`${BASE}/${id}`, payload);
  return data.data;
}

export async function confirmarNomina(id: number): Promise<void> {
  await api.post(`${BASE}/${id}/confirmar`);
}

export async function eliminarNomina(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

export async function generarResumenIa(
  id: number,
): Promise<{ ok: boolean; observaciones_ia?: string; message?: string }> {
  const { data } = await api.post<{
    ok: boolean;
    observaciones_ia?: string;
    message?: string;
  }>(`${BASE}/${id}/resumen-ia`);
  return data;
}

export async function descargarNominaPdf(id: number): Promise<void> {
  const res = await api.get(`${BASE}/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.download = `nomina-${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
